import React, { createContext, useContext } from "react";
import branch from "recompose/branch";
import { graphql } from "react-apollo";
import ShopQuery from "./ShopQuery.gql";

const ShopContext = createContext(null);

const BaseProvider = ({ shop, loading, error, children }) => {
  if (loading) {
    return null;
  }
  if (error) {
    throw new Error("Failed to load store");
  }
  return <ShopContext.Provider value={shop}>{children}</ShopContext.Provider>;
};

const useShop = () => {
  return useContext(ShopContext);
};

const withShop = () => {
  return (BaseComponent) => (props) => {
    const shop = useShop();
    return <BaseComponent {...props} shop={shop} />;
  };
};

const ShopConsumer = ShopContext.Consumer;

/**
 * @typedef {Object} Shop
 */

/**
 * @typedef {Object} ShopQueryGraphQLPropsOptions
 * @property {import('react-apollo').DataValue<{ shop: Shop, loading: boolean, error: import('apollo-client').ApolloError }, {}>} data
 * @property {Object} ownProps
 */

/**
 * @typedef {Object} ShopQueryGraphQLExtraProps
 * @property {Shop} shop
 * @property {boolean} loading
 * @property {import('apollo-client').ApolloError} error
 */

/**
 * @callback ShopQueryGraphQLPropsCallback
 * @param {ShopQueryGraphQLPropsOptions} param0
 * @returns {ShopQueryGraphQLExtraProps}
 */

const ShopProvider = branch(
  (props) => !props.shop,
  graphql(ShopQuery, {
    props: /** @type {ShopQueryGraphQLPropsCallback} */ ({ data }) => {
      return {
        shop: data.loading || data.error ? null : data.shop,
        loading: data.loading,
        error: data.error,
      };
    },
  })
)(BaseProvider);

export { ShopProvider, useShop, withShop, ShopConsumer };
